import type { ReactNode } from "react";
import { toast } from "sonner";
import { i18n } from "@/lib/i18n";

type Action = { label: ReactNode; onClick: () => void };
type Opts = { description?: ReactNode; action?: Action; id?: string | number; duration?: number };

const t = (k: string) => i18n.t(k as never) as string;

/**
 * Consistent toasts: one line of plain words, an optional description, at most one action.
 * `notify.error` stays a little longer. Placement is set once in the app providers
 * (top centre on desktop, bottom centre above the tab bar on phones).
 */
export const notify = {
  success: (message: ReactNode, o: Opts = {}) => toast.success(message, o),
  info: (message: ReactNode, o: Opts = {}) => toast(message, o),
  warning: (message: ReactNode, o: Opts = {}) => toast.warning(message, o),
  error: (message: ReactNode, o: Opts = {}) => toast.error(message, { duration: 6000, ...o }),
  dismiss: (id?: string | number) => toast.dismiss(id),
};

/**
 * The reversible-action toast: "Hidden 3 comments · Undo". Use it instead of a confirm dialog for anything that can be
 * taken back. `undo` may be async; on failure an error toast says so. Returns the toast id.
 *
 *   toastUndo({ message: t("inbox.hidden", { count }), undo: () => api.unhide(ids) })
 */
export function toastUndo({ message, description, undo, ms = 6000, undoneMessage }: { message: ReactNode; description?: ReactNode; undo: () => unknown | Promise<unknown>; ms?: number; undoneMessage?: ReactNode }) {
  const id = toast.success(message, {
    description,
    duration: ms,
    action: {
      label: t("kit.undo"),
      onClick: async () => {
        try {
          await undo();
          toast.success(undoneMessage ?? t("kit.undone"), { duration: 2500 });
        } catch {
          toast.error(t("kit.undoFailed"));
        }
      },
    },
  });
  return id;
}
